import * as THREE from "three"
import { PerformanceManager } from "./performanceManager.js"
import { getGPUTier } from "detect-gpu"
import { GUI } from "dat.gui"
import { Globe } from "./Globe.js"
import {
    FooterGlue,
    FooterSpacer,
    dispatchFooterChangeEvent,
    watchForUIResizing,
} from "./Footer"
import { TextureInitializer } from "./WebMultiMaterial.js"
import CameraSynchronizer, {
    latToGlobePolar,
    lngToGlobeAzimuthal,
} from "./CameraSynchronizer.js"
import MapGlobeSwapper from "./MapGlobeSwapper.js"
import polylabel from "polylabel"
import { geoArea, geoBounds } from "d3-geo"
import {
    handleLatLngClick,
    enableOSMUIControls,
    setTutorial,
    reset as osmReset,
} from "./osm-lib.js"
import * as maplibregl from "maplibre-gl"
import { type MapLibreZoomEvent } from "maplibre-gl"
import { Map as GLMap } from "./Map.js"
import $ from "jquery"
import { LandingPageTutorial } from "./tutorial.js"
import {
    switchToCountyList,
    scrollBottomBarToHighlighted,
} from "./UIHelpers.js"
import {
    STATE_FIPS_BY_ABBR,
    STATE_FIPS_BY_NAME,
    STATE_INFO_BY_FIPS,
} from "./data/FIPS.js"
import { Region } from "./Region.js"
import { type Polygon } from "geojson"
import {
    type RegionLoadEventDetail,
    type RegionLoadEvent,
    type RegionLoadEventHandler,
} from "./@types/events"
// import { fit, getViewRect, debugDrawRect } from "./getViewableRect.js"
import { mapBoundsContainHack } from "./hacks"
import { cutout } from "./geojsonHelpers.js"
import {
    type MapHistoryState,
    updateHistory,
    HistoryStateType,
    getPrevHistoryState,
    checkPopstateInaction,
    notifyPopstateInaction,
    setPrevHistoryState,
    globeHistoryState,
    urlToHistoryState,
    historyStateToURL,
    mapHistoryState,
} from "./GlobeHistory.js"
import { getTweenGroup } from "./ThreeObjectUtils.js"
import { DEG2RAD } from "three/src/math/MathUtils.js"
import { fitPointToViewportCenter } from "./getViewableRect.js"
// import Stats from "stats.js"

declare global {
    interface Window {
        smoothlyGoToMapPoint: (
            lat: number,
            lng: number,
            zoom: number,
            usCounty: string,
            usState: string,
            options: SmoothlyGoToMapPointOptions
        ) => Promise<void>
        smoothlyGoToViewPoint: (lat: number, lon: number, zoom: number) => void
        paused: boolean

        // This is for debug-purposes only
        swapper: MapGlobeSwapper
    }
}

enum BoundKind {
    NONE,
    COUNTY,
    POLYGON,
}

interface SmoothlyGoToMapPointOptions {
    modifyHistory?: boolean
    abortController?: AbortController
}

interface EnterMapOptions {
    lat: number
    lng: number
    zoom: number
    usCounty: string
    usState: string
    doFlyTo?: boolean // default to true
    boundKind?: BoundKind // default to BoundKind.COUNTY
    waitForZoomToBoundMap?: boolean // default to true
    modifyHistory?: boolean // default to true
    signal?: AbortSignal
}

// get URL params
// TODO: Remove this before production
const pageUrl = new URL(window.location.href)
const URL_PARAMS = pageUrl.searchParams
const DEBUG = pageUrl.searchParams.get("debug") === "true"
const settings = {
    hoverColor: {
        emissive: "#2b1c05",
        color: "#ffffff",
    },
}
const returnToGlobePopup = new maplibregl.Popup({
    closeOnClick: false,
    closeButton: false,
    focusAfterOpen: false,
}).setHTML("⬆ Return to Globe")

type MapBounds = [[number, number], [number, number]]

interface MapState {
    immersed: boolean
    visible: boolean
    visibleSoon: boolean
    autoLeave: boolean
    bounded: boolean
    boundsPromise: Promise<MapBounds> | null
    boundsPromiseSignal: AbortSignal | null
    bounds: MapBounds
}

const state: { map: MapState } = {
    map: {
        /**
         * True when the user is fully in the map and the globe is not visible
         */
        immersed: false,
        visible: false,
        visibleSoon: false,
        /**
         * When true, will automatically go from map view to globe view if the user zooms out far enough
         */
        autoLeave: true,
        bounded: false,
        boundsPromise: null,
        boundsPromiseSignal: null,
        bounds: [
            [NaN, NaN],
            [NaN, NaN],
        ],
    },
}

function disableAutoLeave(): void {
    state.map.autoLeave = false
}

function enableAutoLeave(): void {
    state.map.autoLeave = true
}

interface SetMapBoundsOptions {
    signal?: AbortSignal
}

function setMapBounds(
    bounds: Promise<MapBounds>,
    { signal }: SetMapBoundsOptions
): void {
    state.map.boundsPromise = bounds
    state.map.boundsPromiseSignal = signal !== undefined ? signal : null
}

interface EnableMapBoundsOptions {
    signal?: AbortSignal
}

async function enableMapBounds({
    signal,
}: EnableMapBoundsOptions = {}): Promise<void> {
    if (state.map.boundsPromise === null) {
        throw new Error(
            "enableMapBounds called but state.map.boundsPromise was null"
        )
    }

    if (signal === undefined) {
        signal =
            state.map.boundsPromiseSignal !== null
                ? state.map.boundsPromiseSignal
                : undefined
    }

    signal?.throwIfAborted()

    // Save the current bounds promise before awaiting
    const savedMapBoundsPromise = state.map.boundsPromise

    let bounds
    try {
        bounds = await state.map.boundsPromise
    } catch (err) {
        // Bounds promise rejected, probably due to an error/abort of a map click (handleLatlngClick)
        console.warn(err, "map bounds promise rejected")
        return
    }

    signal?.throwIfAborted()

    // Check that the saved bounds promise matches with the most current one
    if (savedMapBoundsPromise !== state.map.boundsPromise) {
        // The bounds promise has been replaced by a new one - abort
        throw new Error("Bounds promise superceded")
    }

    // eslint-disable-next-line no-autofix/prefer-const
    let [[w, s], [e, n]] = bounds

    if (isNaN(w) || isNaN(s) || isNaN(e) || isNaN(n)) {
        console.error({ bounds }, "NaN in map bounds")
        return
    }

    const lngRange = e - w
    const latRange = n - s

    // Add % margin
    w -= lngRange * 0.05
    e += lngRange * 0.05
    s -= latRange * 0.05
    // n += 1

    map.setMaxBounds([
        [w, s],
        [e, n],
    ])

    state.map.bounded = true
    state.map.bounds = bounds
}

async function updateMapBounds(): Promise<void> {
    if (state.map.boundsPromise !== null) await enableMapBounds()
}

window.addEventListener("resize", () => {
    void updateMapBounds()
})

// document.addEventListener("footerchange", () => {
//     void updateMapBounds()
// })

function disableMapBounds(): void {
    state.map.bounded = false
    state.map.boundsPromise = null
    state.map.bounds = [
        [NaN, NaN],
        [NaN, NaN],
    ]
    map.setMaxBounds()
}

function querySelectorThrow<K extends keyof HTMLElementTagNameMap>(
    selectors: K
): HTMLElementTagNameMap[K]
function querySelectorThrow<K extends keyof SVGElementTagNameMap>(
    selectors: K
): SVGElementTagNameMap[K]
function querySelectorThrow<K extends keyof MathMLElementTagNameMap>(
    selectors: K
): MathMLElementTagNameMap[K]
/** @deprecated */
function querySelectorThrow<K extends keyof HTMLElementDeprecatedTagNameMap>(
    selectors: K
): HTMLElementDeprecatedTagNameMap[K]
function querySelectorThrow<E extends Element = Element>(selectors: string): E

function querySelectorThrow<T extends Element>(
    ...parameters: Parameters<typeof document.querySelector>
): Exclude<ReturnType<typeof document.querySelector>, null> {
    const res = document.querySelector<T>(...parameters)
    if (res === null) {
        throw new ReferenceError(
            `querySelector (${parameters.join(", ")}) returned null`
        )
    }
    return res
}

interface BoundMapOptions {
    signal?: AbortSignal
}

function boundMap(polygon: Promise<Polygon>, { signal }: BoundMapOptions): void
function boundMap(polygon: Polygon, { signal }: BoundMapOptions): void
function boundMap(county: Region, { signal }: BoundMapOptions): void
function boundMap(
    countyOrPolygon: Region | Polygon | Promise<Polygon>,
    { signal }: BoundMapOptions
): void {
    let rawBoundsPromise: Promise<MapBounds>
    if (countyOrPolygon instanceof Promise) {
        rawBoundsPromise = countyOrPolygon.then((polygon) => {
            signal?.throwIfAborted()
            return geoBounds(polygon) as MapBounds
        })
    } else if (
        "type" in countyOrPolygon &&
        countyOrPolygon.type === "Polygon"
    ) {
        rawBoundsPromise = Promise.resolve(
            geoBounds(countyOrPolygon) as MapBounds
        )
    } else if (countyOrPolygon instanceof Region) {
        rawBoundsPromise = Promise.resolve([
            [
                countyOrPolygon.face.userData.minx as number,
                countyOrPolygon.face.userData.miny as number,
            ],
            [
                countyOrPolygon.face.userData.maxx as number, // Extra maxx because the right bar occludes some of the right
                countyOrPolygon.face.userData.maxy as number,
            ],
        ])
    } else {
        throw new TypeError("boundMap expects Region or Polygon")
    }

    setMapBounds(rawBoundsPromise, { signal })
}

const fpsClock = new THREE.Clock()

/**
 Example gpuTier:
 {
 "tier": 1,
 "isMobile": false,
 "type": "BENCHMARK",
 "fps": 21,
 "gpu": "intel iris graphics 6100"
 }
 */
let gpuTier
let gui
let GLOBE: Globe
let TEXTURE_INITIALIZER: TextureInitializer
let scene: THREE.Scene
let renderer: THREE.WebGLRenderer
let screenQuad: THREE.Mesh
let elapsedTime = 0
let cameraSynchronizer: CameraSynchronizer
let map: GLMap
let swapper: MapGlobeSwapper

// const stats = new Stats()
// stats.showPanel(0)
// document.body.appendChild(stats.dom)

// Run init when DOM is ready
$(() => {
    void init()
})

interface MapInitParams {
    returnToGlobePopup: maplibregl.Popup
    resetButton: HTMLElement
    enableAutoLeave: () => void
    enableMapBounds: (EnableMapBoundsOptions) => Promise<void>
}
// TODO: Return map, return promise? return SOMETHING
function initializeMap({
    returnToGlobePopup,
    resetButton,
    enableAutoLeave,
    enableMapBounds,
}: MapInitParams): [GLMap, Promise<[string, string]>] {
    const osmServerApiHost = "/api"
    const defaultPitch = 23 // degrees
    const map = new GLMap({
        container: "map", // container id
        style: `${osmServerApiHost}/maps/R3/style.json`, // style URL
        center: [-86.42816507327348, 35.84716452652847], // starting position [lng, lat]
        zoom: 6.24, // starting zoom
        pitch: defaultPitch,
        bearing: 0,
        maxPitch: 85,
        maxZoom: 20,
        // maxBounds:new maplibregl.LngLatBounds(
        //     new maplibregl.LngLat(lng - 1, lat - 1),
        //     new maplibregl.LngLat(lng + 1, lat + 1)
        // )
    })

    // Change map bounds from fit to cover ?
    mapBoundsContainHack(map)

    // Hook up return to globe button
    map.on("mouseover", "county-cutout", (event) => {
        returnToGlobePopup.addTo(map)
        returnToGlobePopup.setLngLat(event.lngLat)
    })
    map.on("mousemove", "county-cutout", (event) => {
        returnToGlobePopup.setLngLat(event.lngLat)
    })
    map.on("mouseout", "county-cutout", (event) => {
        returnToGlobePopup.remove()
    })
    map.on("click", "county-cutout", (event) => {
        // Do up to globe
        querySelectorThrow<HTMLAnchorElement>(
            `#county_list li.selected span`
        ).click()
    })
    returnToGlobePopup.on("open", () => {
        map.getCanvas().style.cursor = "pointer"
    })
    returnToGlobePopup.on("close", () => {
        map.getCanvas().style.cursor = ""
    })

    // Hack to move mapgl attribution in the DOM
    const attribution = querySelectorThrow(".maplibregl-ctrl-attrib")
    const centerWindow = querySelectorThrow("#center-window")
    centerWindow.append(attribution)
    // Hide attribution at first
    attribution.classList.add("hidden")
    resetButton.addEventListener("click", () =>
        attribution.classList.add("hidden")
    )

    const firstSymbolPromise = new Promise<[string, string]>(
        (resolve, reject) =>
            map.on("load", () => {
                const layers = map.getStyle().layers
                // Find the index of the first symbol layer in the map style
                let firstSymbolId
                for (let i = 0; i < layers.length; i++) {
                    if (layers[i].type === "symbol") {
                        firstSymbolId = layers[i].id
                        break
                    }
                }
                let firstLineId
                for (let i = 0; i < layers.length; i++) {
                    if (layers[i].type === "line") {
                        firstLineId = layers[i].id
                        break
                    }
                }
                resolve([firstSymbolId, firstLineId])
            })
    )

    map.on(
        "zoomend",
        (
            event: MapLibreZoomEvent & { cause: string; signal?: AbortSignal }
        ) => {
            if (event.signal?.aborted === true) {
                return
            }
            // Enable autoLeave when a zoom ends.
            // This is mostly for avoiding premature autoLeave during initial zoom
            // Also helps achieve the "any user input enables auto leave" function
            if (
                (event.cause === "enterMap" ||
                    event.cause === "smoothlyGoToMapPoint") &&
                state.map.immersed
            ) {
                enableAutoLeave()
                void enableMapBounds({ signal: event.signal })
            }
        }
    )

    map.on(
        "zoomend",
        (
            event: MapLibreZoomEvent & { cause: string; signal?: AbortSignal }
        ) => {
            if (event.signal?.aborted === true) {
                return
            }
            // Enable autoLeave when a zoom ends.
            // This is mostly for avoiding premature autoLeave during initial zoom
            // Also helps achieve the "any user input enables auto leave" function
            if (
                (event.cause === "enterMap" ||
                    event.cause === "smoothlyGoToMapPoint") &&
                state.map.immersed
            ) {
                enableAutoLeave()
                void enableMapBounds({ signal: event.signal })
            }
        }
    )

    return [map, firstSymbolPromise]
}

interface ConnectMapGlobeParams {
    GLOBE: Globe
    map: GLMap
    resetButton: HTMLElement
    tutorial: LandingPageTutorial
    firstSymbolPromise: Promise<[string, string]>
}
// There's circular dependencies
// so I'm separating all init code that requires both the map and globe into this function
// TODO: Return swapper, cameraSynchronizer
function connectMapGlobe({
    GLOBE,
    map,
    resetButton,
    tutorial,
    firstSymbolPromise,
}: ConnectMapGlobeParams): [
    MapGlobeSwapper,
    CameraSynchronizer,
    (options: EnterMapOptions) => void
] {
    // This only depends on map
    // but it's here because it's a dependency of later globe+map code
    // I could put it in initializeMap but then I'd have to return it from that function
    // and add it to this one as a parameter

    map.transform._fov = GLOBE.camera.fov * THREE.MathUtils.DEG2RAD
    map.transform._unmodified = false
    map.transform._calcMatrices()

    const zipLabel = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: "z-2 map_zip_popup",
    })
    zipLabel.on("open", (event) => {
        querySelectorThrow("#popup-container").append(
            querySelectorThrow(".maplibregl-popup")
        )
    })

    cameraSynchronizer = new CameraSynchronizer(GLOBE.controls, map)
    GLOBE.connectCameraSynchronizer(cameraSynchronizer)
    const swapper = new MapGlobeSwapper(
        GLOBE,
        map,
        "fade-out",
        "fade-in",
        cameraSynchronizer
    )

    const { onRegionLoad, enterMap } = createGlobeFuncs({
        zipLabel,
        map,
        resetButton,
        swapper,
        tutorial,
    })

    // Show/hide attribution depending on whether map data is visible
    const attribution = querySelectorThrow(".maplibregl-ctrl-attrib")
    GLOBE.addEventListener("entercounties", () => {
        attribution.classList.remove("hidden")
    })
    GLOBE.addEventListener("regionload", (event: RegionLoadEvent) => {
        state.map.visibleSoon = true
        firstSymbolPromise
            .then(async ([firstSymbolId, firstLineId]: [string, string]) => {
                await onRegionLoad(event, firstSymbolId, firstLineId)
            })
            .catch((err) => {
                console.error(err)
            })
    })
    GLOBE.addEventListener("mapclick", (event: CustomEvent) => {
        map.getCanvas().dispatchEvent(
            new PointerEvent("click", event.detail.pointerUpEvent)
        )
    })
    GLOBE.addEventListener("mapenter", (event) => {
        state.map.visible = true
        state.map.visibleSoon = false
    })
    GLOBE.addEventListener("mapleave", (event) => {
        state.map.visible = false
        zipLabel.remove()
    })

    GLOBE.addEventListener("regionraise", (event: CustomEvent) => {
        cameraSynchronizer.handleMeshRaise(event.detail)
    })

    GLOBE.getCanvas().addEventListener("mousemove", (e) => {
        if (state.map.visible) {
            const eventCopy = new MouseEvent("mousemove", e)
            map.getCanvas().dispatchEvent(eventCopy)
        }
    })

    return [swapper, cameraSynchronizer, enterMap]
}

async function init(): Promise<void> {
    watchForUIResizing()
    // Hook into transition events to send footer change events every frame
    let transitions = 0
    document.addEventListener("transitionstart", (event) => {
        transitions += 1
    })
    document.addEventListener("transitionend", (event) => {
        transitions -= 1
        dispatchFooterChangeEvent()
    })
    document.addEventListener("transitioncancel", (event) => {
        transitions -= 1
        dispatchFooterChangeEvent()
    })

    function fn(): void {
        requestAnimationFrame(fn)
        if (transitions > 0) {
            console.log("TRANSITIONS", transitions)
            dispatchFooterChangeEvent()
        }
    }

    fn()

    // window.addEventListener("beforeunload", (e) => {
    //     const loadingScreen = document.getElementById("loading-screen")
    //     if (loadingScreen !== null) {
    //         loadingScreen.style.visibility = "visible"
    //     }
    // })

    gpuTier = await getGPUTier()
    // Debug GUI stuff
    if (DEBUG) {
        gui = new GUI()
        gui.domElement.parentElement.style.zIndex = "1000"

        const rolloverFolder = gui.addFolder("rollover color")
        rolloverFolder.addColor(settings.hoverColor, "color")
        rolloverFolder.addColor(settings.hoverColor, "emissive")
        rolloverFolder.open()
    }

    fpsClock.start()

    // Glue the UI buttons to the footer
    // eslint-disable-next-line no-new
    new FooterGlue(
        querySelectorThrow(".btn-column.absolute.control"),
        document.documentElement.clientHeight - 56
    )
    // eslint-disable-next-line no-new
    new FooterGlue(
        querySelectorThrow(".walkthrough-question-mark-container"),
        document.documentElement.clientHeight - 65
    )
    // eslint-disable-next-line no-new
    new FooterSpacer(querySelectorThrow(".footer-spacer"))

    // Monkey patch THREE.Object3D.updateMatrixWorld
    // Adds a short circuit when the object is invisible
    // We have a lot of invisible objects, so this improves performance
    ;(function () {
        const _updateMatrixWorld = THREE.Object3D.prototype.updateMatrixWorld
        THREE.Object3D.prototype.updateMatrixWorld = function () {
            if (this.visible === false) {
                return
            }
            _updateMatrixWorld.apply(this)
        }
    })()

    scene = new THREE.Scene()

    //* ======Render Settings=======*/
    const renderSettings = {
        antialias: true,
        preserveDrawingBuffer: true,
        alpha: true,
    }

    if (gpuTier.isMobile === true) {
        renderSettings.antialias = false
    }

    const renderHeight = document.documentElement.clientHeight
    const renderWidth = document.documentElement.clientWidth

    THREE.ColorManagement.enabled = false

    renderer = new THREE.WebGLRenderer(renderSettings)
    const container = querySelectorThrow<HTMLDivElement>("#globe")
    container.appendChild(renderer.domElement)

    renderer.setPixelRatio(window.devicePixelRatio)
    renderer.setSize(renderWidth, renderHeight)
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 1
    // renderer.outputEncoding = THREE.sRGBEncoding
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.shadowMap.enabled = true
    renderer.setClearColor("rgb(20, 20, 22)", 1)

    screenQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2))
    screenQuad.frustumCulled = false
    screenQuad.renderOrder = -1
    scene.add(screenQuad)

    const performanceManager = new PerformanceManager(renderer, gpuTier)

    // Performance overlay
    if (URL_PARAMS.get("showPerfLevel") === "1") {
        gui = new GUI()
        gui.domElement.parentElement.style.zIndex = "1000"
        // Create performance folder
        {
            const folder = gui.addFolder("Performance")
            folder
                .add(
                    performanceManager.performanceMonitorData,
                    "currentQuality"
                )
                .listen()
            folder.open()
        }
        // Create renderer info folder
        {
            const folder = gui.addFolder("Renderer Info")
            folder.add(renderer.info.memory, "geometries").listen()
            folder.add(renderer.info.memory, "textures").listen()
            folder.add(renderer.info.render, "calls").listen()
            folder.add(renderer.info.render, "triangles").listen()
            folder.add(renderer.info.render, "points").listen()
            folder.add(renderer.info.render, "lines").listen()
            folder.add(renderer.info.render, "frame").listen()
            folder.open()
        }
        {
            const folder = gui.addFolder("Shadows")
            folder.add(renderer.shadowMap, "enabled").listen()
        }
    }

    const resetButton = querySelectorThrow<HTMLElement>("#globe_reset_button")

    const tutorial = new LandingPageTutorial()

    // TODO: Extract logic that isn't explicitly tutorial-related from these tutorial event handlers
    // On tutorial county explanation
    tutorial.addEventListener("countyexplanation", () => {
        // Minimize walkthrough ui
        // querySelectorThrow(".how-it-works").classList.add("minimized")
        // querySelectorThrow(
        //     ".walkthrough-question-mark-container"
        // ).classList.add("minimized")

        // // Fade out "build your ballot" text
        // querySelectorThrow(".build-your-ballot-wrapper").classList.add(
        //     "fade-out"
        // )

        // Hide left panel
        GLOBE.leftPanel.hide()

        // Slide canvas back in place
        querySelectorThrow("#popup-container").classList.remove("nudge-right")
        querySelectorThrow("#map").classList.remove("nudge-right")
        GLOBE.canvas.classList.remove("nudge-right")
        GLOBE.labelRenderer.domElement.classList.remove("nudge-right")
        GLOBE.globe_svg_container.classList.remove("nudge-right")
    })

    // On tutorial zip explanation
    tutorial.addEventListener("zipexplanation", () => {
        // Show left panel
        GLOBE.leftPanel.show()

        // Slide canvas to the right to keep globe elements centered
        querySelectorThrow("#popup-container").classList.add("nudge-right")
        querySelectorThrow("#map").classList.add("nudge-right")
        GLOBE.canvas.classList.add("nudge-right")
        GLOBE.labelRenderer.domElement.classList.add("nudge-right")
        GLOBE.globe_svg_container.classList.add("nudge-right")
    })

    // On tutorial reset
    tutorial.addEventListener("reset", () => {
        // Maximize walkthrough ui
        // querySelectorThrow(".how-it-works").classList.remove("minimized")
        // querySelectorThrow(
        //     ".walkthrough-question-mark-container"
        // ).classList.remove("minimized")

        // // Bring back "build your ballot" text
        // querySelectorThrow(".build-your-ballot-wrapper").classList.remove(
        //     "fade-out"
        // )

        // Hide left panel
        GLOBE.leftPanel.hide()

        // Slide canvas back in place
        querySelectorThrow("#popup-container").classList.remove("nudge-right")
        querySelectorThrow("#map").classList.remove("nudge-right")
        GLOBE.canvas.classList.remove("nudge-right")
        GLOBE.labelRenderer.domElement.classList.remove("nudge-right")
        GLOBE.globe_svg_container.classList.remove("nudge-right")
    })

    TEXTURE_INITIALIZER = new TextureInitializer(renderer, 2)

    setTutorial(tutorial)

    const camera = new THREE.PerspectiveCamera(40, 1, 0.03, 300)

    GLOBE = new Globe(
        scene,
        renderer.domElement,
        resetButton,
        performanceManager,
        gpuTier,
        settings,
        TEXTURE_INITIALIZER,
        tutorial,
        camera
    )

    const globeInteractivePromise = new Promise((resolve) => {
        window.addEventListener("globeinteractive", resolve, { once: true })
    })

    /// //////////////////
    // Set up maplibregl
    /// //////////////////
    function doMapSetup(): (options: EnterMapOptions) => void {
        let firstSymbolPromise: Promise<[string, string]>
        {
            const ret = initializeMap({
                returnToGlobePopup,
                resetButton,
                enableAutoLeave,
                enableMapBounds,
            })
            // Set global variable
            map = ret[0]
            firstSymbolPromise = ret[1]
        }

        let enterMap: (options: EnterMapOptions) => void
        // Set up everything that requires both map and globe
        {
            const ret = connectMapGlobe({
                GLOBE,
                map,
                resetButton,
                tutorial,
                firstSymbolPromise,
            })
            // Set global variables
            swapper = ret[0]
            cameraSynchronizer = ret[1]
            enterMap = ret[2]
        }
        return enterMap
    }
    const historyState = urlToHistoryState(window.location.href)
    let enterMapPromise: Promise<(options: EnterMapOptions) => void>
    if (historyState.type === HistoryStateType.map) {
        // If we're in a map state, immediately set up map
        enterMapPromise = Promise.resolve(doMapSetup())
    } else {
        // If we're in a globe state, defer map load until after globe is interactive
        // Reduces apparent load time
        enterMapPromise = globeInteractivePromise.then(doMapSetup)
    }

    void initFromURLParams(GLOBE, {
        enterMapPromise,
        tutorial,
    })

    // This is for debug-purposes only
    window.swapper = swapper

    // Add screen quad
    THREE.Cache.enabled = true
    Promise.all([
        fetch("/assets/shaders/screenquad.vert").then(
            async (response) => await response.text()
        ),
        fetch("/assets/shaders/screenquad.frag").then(
            async (response) => await response.text()
        ),

        new Promise((resolve, reject) => {
            const texture = new THREE.Texture()
            const img = document.querySelector<HTMLImageElement>(
                "#globeBackgroundImagePreload"
            )
            texture.image = img
            texture.needsUpdate = true
            resolve(texture)
        }),
    ])
        .then(([vert, frag, tex]: [string, string, THREE.Texture]) => {
            const uniforms = {
                tex: { type: "t", value: tex },
                aspect: {
                    type: "f",
                    // Hardcoded to avoid an issue where this code was run before the image fully loaded,
                    // causing a NaN to get passed into the shader, which caused the image to display as vertical bars.
                    // This will have to be changed manually if the image is updated
                    // Or you could write some code that handles the case where the image isn't fully loaded and updates the uniform when the image gets loaded :)
                    // value: tex.image.width / tex.image.height,
                    value: 1920 / 1080,
                },
                screenAspect: { type: "f", value: GLOBE.camera.aspect },
                // texHeight: { type: "i", value: tex.image.height },
            }
            const mat = new THREE.ShaderMaterial({
                vertexShader: vert,
                fragmentShader: frag,
                depthWrite: false,
                depthTest: false,
                uniforms,
            })
            screenQuad.material = mat

            // Set clear color to transparent
            // This allows the map to be seen under the globe canvas
            // renderer.setClearColor(0, 0)
        })
        .catch((err) => {
            console.error(err)
        })

    /* = =====Light======= */

    const light = new THREE.AmbientLight(0x161718, 15) // soft white light
    light.position.set(0, 75, 0)
    scene.add(light)

    // container.style.touchAction = "none"
    window.addEventListener("resize", onWindowResize, false)
    const canvas = querySelectorThrow("canvas")

    const aspectRatio =
        document.documentElement.clientWidth /
        document.documentElement.clientHeight
    if (aspectRatio < 1.55) {
        canvas.classList.add("tall")
    } else {
        canvas.classList.remove("tall")
    }

    animate(0)
}

// Map URL examples:
// http://localhost:3000/?lng=-83.884804&lat=36.032334&county=Knox&state=Tennessee&district_id=null&election_ilter=null&election_type=0
interface urlParams {
    fips?: FIPSCode
    lng?: number
    lat?: number
    county?: string
    state?: string
    district_id?: string
    election_filter?: string
    election_type?: number
    ballot_screenshot_mode?: string
    animated?: boolean
}

function readURLParams(url: string | URL): urlParams {
    // make sure url is always type URL
    if (typeof url === "string") {
        url = new URL(url)
    }
    const search = url.searchParams
    const params: urlParams = {}
    const paramShapeData = [
        { key: "fips" },
        { key: "lng", transformer: (p: string) => parseFloat(p ?? "") },
        { key: "lat", transformer: (p: string) => parseFloat(p ?? "") },
        { key: "county" },
        { key: "state" },
        { key: "district_id" },
        { key: "election_filter" },
        { key: "election_type" },
        { key: "ballot_screenshot_mode" },
        { key: "animated", transformer: (p: string) => p === "true" },
    ]
    paramShapeData.forEach((shape) => {
        if (search.has(shape.key)) {
            const value =
                shape.transformer != null
                    ? shape.transformer(search.get(shape.key) as string)
                    : search.get(shape.key)
            params[shape.key] = value
        }
    })
    return params
}

interface InitFromURLParamsOptions {
    enterMapPromise: Promise<(options: EnterMapOptions) => void>
    tutorial: LandingPageTutorial
}

async function initFromURLParams(
    globe: Globe,
    options: InitFromURLParamsOptions
): Promise<void> {
    const params = readURLParams(window.location.href)
    // Process URL params
    globe.ballot_screenshot_mode =
        params.ballot_screenshot_mode !== undefined
            ? params.ballot_screenshot_mode
            : null

    let usState = params.state

    /* Calculate usState from URL params */
    if (params.fips !== undefined) {
        // Get just the state part of the fips, ie. 01 from 01023
        const stateFips = params.fips.substring(0, 2)
        if (Object.keys(STATE_INFO_BY_FIPS).includes(stateFips)) {
            usState = STATE_INFO_BY_FIPS[stateFips].name
        }
    } else if (
        usState !== undefined &&
        !Object.keys(STATE_FIPS_BY_NAME).includes(usState)
    ) {
        // Treat state like it's an abbreviation
        if (usState in STATE_FIPS_BY_ABBR) {
            usState = STATE_FIPS_BY_ABBR[usState].name
        } else {
            usState = ""
        }
    }

    /* Set initial history state */
    const historyState = urlToHistoryState(window.location.href)
    if (history.state === null) {
        historyState.prev = null
        history.replaceState(historyState, "", historyStateToURL(historyState))
    }

    /* Set globe state */
    if (historyState.type === HistoryStateType.initial) {
        // Case: initial load
        // Swing the camera towards the USA
        globe.controls.focus(new THREE.Vector3(0, 51, 1.5), { animated: false })
    } else if (
        historyState.type === HistoryStateType.globe ||
        historyState.type === HistoryStateType.map
    ) {
        if (params.animated === true) {
            if (params.lat === undefined || params.lng === undefined) {
                console.error({ params }, "lat or lng was undefined")
                return
            }
            if (isNaN(params.lat) || isNaN(params.lng)) {
                console.error({ params }, "lat or lng was NaN")
                return
            }

            if (usState === undefined) {
                throw new Error("No state search param")
            }

            const county = params.county

            if (county === undefined) {
                throw new Error("No county search param")
            }
            // globe.controls.setPolarAngle(params.lat * DEG2RAD)
            // globe.controls.setAzimuthalAngle(params.lng * DEG2RAD)
            const polar = latToGlobePolar(params.lat)
            const azimuthal = lngToGlobeAzimuthal(params.lng)

            globe.controls.setAzimuthalAngle(azimuthal)
            globe.controls.setPolarAngle(polar)
            // Wait till both countries and states are loaded before zooming in
            await globe.loadStatesPromise
            await globe.loadCountriesPromise
            // Wait just a bit extra for good measure
            await new Promise((resolve) => {
                setTimeout(resolve, 100)
            })
            void window.smoothlyGoToMapPoint(
                params.lat,
                params.lng,
                13.14,
                county,
                usState,
                {
                    modifyHistory: true,
                }
            )
            return
        }
        // Case: county or zip mode
        // Set "state clicked" state variable
        globe.state.aStateHasBeenClicked = true

        // Globe loads states and countries by itself during constructor

        // Wait till the state list and states are loaded, then load counties
        const countyLoadPromise = Promise.all([
            globe.loadStatesPromise,
            globe.addStateListPromise,
        ])
            .then(async (results) => {
                const stateMesh = globe.getStateByName(usState as string)

                /* Put state list into state mode */
                const list = document.querySelector(".state_list")
                if (list === null) {
                    console.error("State list was null")
                    return
                }
                list.classList.remove("hidden")
                const button = document.querySelector(
                    `.state_list ul li#${stateMesh.userData.abbr as string}`
                )
                if (button === null) {
                    console.error("Button with abbr not found")
                    return
                }
                button.classList.add("highlighted")
                setTimeout(() => {
                    scrollBottomBarToHighlighted("state_list")
                }, 1000)
                /* Load counties */
                const doZoom =
                    params.fips !== undefined &&
                    (params.fips === null ||
                        params.fips === "" ||
                        !(params.fips.length === 5))
                const [loadCountiesPromise, raisePromise] =
                    // County list is replaced by state list here

                    globe.loadCountiesWithRaisePromise(stateMesh, doZoom, {
                        modifyHistory: false,
                        modifyTutorial:
                            historyState.type === HistoryStateType.globe,
                    })
                globe.loadCountiesPromise = loadCountiesPromise
                return await Promise.all([loadCountiesPromise, raisePromise])
            })
            .then((gltf) => {
                if (
                    params.fips === null ||
                    params.fips === "" ||
                    params.fips === undefined
                )
                    return

                if (gltf === null || gltf === undefined) return

                /* load zips */

                if (params.fips.length === 5) {
                    // Load zip
                    globe.COUNTY_GROUP.traverse((obj) => {
                        if (!(obj instanceof THREE.Mesh)) return

                        if (obj.name === params.fips) {
                            void globe.loadZips(obj, { modifyHistory: false })
                        }
                    })
                }
            })
            .catch((err) => {
                console.error(err)
                throw err
            })

        /* End of globe mode stuff, now do map mode */
        if (historyState.type !== HistoryStateType.map) {
            return
        }

        if (params.lat === undefined || params.lng === undefined) {
            console.error({ params }, "lat or lng was undefined")
            return
        }
        if (isNaN(params.lat) || isNaN(params.lng)) {
            console.error({ params }, "lat or lng was NaN")
            return
        }

        if (usState === undefined) {
            throw new Error("No state search param")
        }

        const county = params.county

        if (county === undefined) {
            throw new Error("No county search param")
        }

        if (usState.length === 2) {
            usState = STATE_FIPS_BY_ABBR[usState].name
            if (usState === null) {
                throw new Error("couldn't find state fips by abbr")
            }
        }
        const abbr = STATE_FIPS_BY_NAME[usState].abbr

        const zoom = 13.24

        // This won't be available until both the map and the globe have been initialized
        const enterMap = await options.enterMapPromise
        // Swapper gets initialized w/ map
        swapper.swapToMapImmediately()
        map.jumpTo({ center: [params.lng, params.lat], zoom })
        enterMap({
            lat: params.lat,
            lng: params.lng,
            zoom: 13.24,
            usCounty: county,
            usState,
            doFlyTo: true,
            boundKind: BoundKind.POLYGON,
            waitForZoomToBoundMap: false,
            modifyHistory: false,
        })
        // Go straight to the map page

        enableAutoLeave()
        GLOBE.leftPanel.show()

        const ring = querySelectorThrow<HTMLElement>(".globe_ring")
        ring.style.transform =
            "translate(-50%, -50%) scale(100) rotate(30.96deg)"

        // Show attribution
        const attribution = querySelectorThrow(".maplibregl-ctrl-attrib")
        attribution.classList.remove("hidden")

        // Show bottom bar
        const navList = querySelectorThrow(".navigation_list")

        // Slide canvas to the right to keep globe elements centered
        querySelectorThrow("#popup-container").classList.add("nudge-right")
        querySelectorThrow("#map").classList.add("nudge-right")
        GLOBE.canvas.classList.add("nudge-right")
        GLOBE.labelRenderer.domElement.classList.add("nudge-right")
        GLOBE.globe_svg_container.classList.add("nudge-right")

        GLOBE.addStateListPromise
            .then(async () => {
                // County list is first added here
                await countyLoadPromise
            })
            .then(async () => {
                return await switchToCountyList(abbr)
            })
            .then(async () => {
                try {
                    const prevSelected = querySelectorThrow(
                        ".county_list .selected"
                    )
                    prevSelected?.classList.remove("selected")
                } catch (e) {
                    // console.error(e)
                }
                const ele = querySelectorThrow(`li#${CSS.escape(county)}`)
                if (ele != null) {
                    ele.classList.add("selected")
                    navList.classList.remove("hidden")
                    querySelectorThrow("#loaded").classList.remove("hidden")
                    scrollBottomBarToHighlighted("county_list")
                }
                GLOBE.dispatchFooterChangeEvent()

                return await Promise.all([
                    GLOBE.loadStatesPromise,
                    GLOBE.addStateListPromise,
                ])
            })
            .then(async () => {
                return await GLOBE.loadCountiesPromise
            })
            .then(() => {
                // cameraSynchronizer.syncGlobe()
                // GLOBE.animate(1000000000)
                // Cancel all tweens
                // GLOBE.scene.traverse((object) => {
                //     try {
                //         getTweenGroup(object)
                //             .getAll()
                //             .forEach((tween) => {
                //                 tween.end()
                //             })
                //     } catch {}
                // })
                // GLOBE.ANIMATIONS.killAll()

                GLOBE.state.aStateHasBeenClicked = true
                GLOBE.state.zoomLevel = 1
                // Lower states to their flat state
                GLOBE.STATE_GROUP.children.forEach((child) => {
                    child.scale.copy(child.userData.loweredScale)
                    child.userData.state = "lowered"
                })

                // Set up county list callbacks
                document
                    .querySelectorAll(".county_list li")
                    .forEach((element) => {
                        const a = element.querySelector("span")
                        const fips = element.getAttribute("fips")
                        if (fips === null) {
                            console.error(
                                "Could not get fips attribute from list element"
                            )
                            return
                        }
                        const mesh = GLOBE.getMeshByFips(fips)
                        if (mesh === null) {
                            console.error(
                                "Could not get mesh associated with fips"
                            )
                            return
                        }

                        if (a === null) {
                            throw new ReferenceError(
                                "County list anchor element not found"
                            )
                        }

                        a.addEventListener("mouseover", (event) => {
                            GLOBE.highlight(mesh)
                        })

                        a.addEventListener("mouseout", (event) => {
                            GLOBE.unhighlight(mesh)
                        })

                        a.addEventListener("click", (event) => {
                            cameraSynchronizer.syncGlobe()
                            GLOBE.animate(100000000)
                            void GLOBE.loadZips(mesh)
                            options.tutorial.state.send("LEAVE_MAP")
                        })
                    })
            })
            .catch((err) => {
                console.log(err)
            })
    }
}

function getLabelPositions(
    geojson,
    precision
): Map<string, [number, number]> | null {
    // Type check
    if (geojson.type !== "FeatureCollection") {
        console.warn(
            "getLabelPositions: geojson type was not FeatureCollection",
            geojson.type
        )
        return null
    }
    const labelPositions = new Map()
    geojson.features.forEach((feature) => {
        if (feature.geometry.type === "Polygon")
            labelPositions.set(
                feature.properties.GEOID20,
                polylabel(feature.geometry.coordinates, precision)
            )
        if (feature.geometry.type !== "MultiPolygon") return null

        // Find the polylabel of the biggest polygon
        const areas = feature.geometry.coordinates.map((coords) =>
            geoArea(coords)
        )
        const biggest = areas.reduce(
            (acc, area, index) => {
                if (area > acc.area) {
                    acc.area = area
                    acc.index = index
                }
                return acc
            },
            { area: -1, index: -1 }
        )
        if (biggest.index === -1) {
            console.warn(
                "Zero-area zip code found at county",
                feature.properties.GEOID,
                "at zip",
                feature.properties.GEOID20
            )
            return null
        }
        labelPositions.set(
            feature.properties.GEOID20,
            polylabel(feature.geometry.coordinates[biggest.index], precision)
        )
    })
    return labelPositions
}

// TODO: Refactor so this returns relevant functions and doesn't need extraneous parameters
// A lot of the logic in here should be external
// Some functions it should return: reset, smoothlyGoToMapPoint
function createGlobeFuncs({
    zipLabel,
    map,
    resetButton,
    swapper,
    tutorial,
}: {
    zipLabel: maplibregl.Popup
    map: GLMap
    resetButton: HTMLElement
    swapper: MapGlobeSwapper
    tutorial: LandingPageTutorial
}): {
    onRegionLoad: RegionLoadEventHandler
    enterMap: (EnterMapOptions) => void
} {
    let lastLoad: RegionLoadEventDetail | null = null
    let lastLoaded: RegionLoadEventDetail | null = null
    let hoveredStateId: string | number | null = null
    let labelPositions: Map<string, [number, number]> | null = null

    waitAndSetUpUICallbacks(0, { tutorial })

    const onMouseEnterZip = function (zip): void {
        const labelPos = labelPositions?.get(zip)

        // TODO: Find solution for zips with no geometry
        if (labelPos != null) {
            zipLabel.setLngLat(labelPos).setHTML(zip).addTo(map)
        }
    }

    function clearMap(): void {
        if (lastLoaded !== null) {
            const { id } = lastLoaded
            map.offAll()
            // Remove zips
            map.removeLayer(`${id}-fills`)
            map.removeLayer(`${id}-borders`)
            lastLoad = null
            lastLoaded = null
            hoveredStateId = null
            labelPositions = null
            map.removeSource(id)
            try {
                disableMapBounds()
            } catch (err) {
                console.error(err)
            }
        }
        if (typeof map.getLayer("county-cutout") !== "undefined") {
            map.removeLayer("county-cutout")
            map.removeSource("county-cutout")
        }
        returnToGlobePopup.remove()
        zipLabel.remove()
    }

    function reset(): void {
        smoothlyGoToMapPointAbortController.abort("reset")
        clearMap()

        // Hide OSM left panel elements
        querySelectorThrow("#filter-row").classList.add("hidden")
        querySelectorThrow("#district-list").classList.add("hidden")
        querySelectorThrow("#election-overview-div").classList.add("hidden")
        querySelectorThrow("#filters").classList.add("hidden")

        osmReset(map)

        // Show Globe left panel elements
        querySelectorThrow("#left-panel-header").classList.remove("hidden")
        querySelectorThrow("#zip-code-container").classList.remove("hidden")

        state.map.immersed = false
        state.map.visible = false
        state.map.visibleSoon = false
    }

    window.addEventListener("popstate", (event) => {
        console.log({ event, prevState: getPrevHistoryState() }, "popstate")
        if (checkPopstateInaction()) {
            notifyPopstateInaction()
            return
        }
        const prevState = getPrevHistoryState()
        if (
            event.state.type === HistoryStateType.initial ||
            (event.state.type === HistoryStateType.globe &&
                prevState !== null &&
                prevState.type === HistoryStateType.map)
        ) {
            reset()
            swapper.swapToGlobe()
        }
        void GLOBE.setFromHistoryState(event.state, false)
        setPrevHistoryState(event.state)
    })

    function enterMap(options: EnterMapOptions): void {
        const {
            lat,
            lng,
            zoom,
            usCounty,
            usState,
            doFlyTo = true,
            boundKind = BoundKind.COUNTY,
            waitForZoomToBoundMap = true,
            modifyHistory = true,
            signal,
        } = options
        tutorial.state.send("ENTER_MAP")
        clearMap()

        // We start at a high enough zoom level to trigger autoLeave, so we want to disable it until the initial zoom finishes.
        disableAutoLeave()

        state.map.immersed = true
        state.map.visible = true

        if (modifyHistory) {
            const historyState = mapHistoryState({
                lon: lng,
                lat,
                usCounty,
                usState,
                districtId: "null",
                electionFilter: "null",
                electionType: 0,
            })
            updateHistory(historyState)
        }

        enableOSMUIControls(map)
        swapper.swapToMap()

        // if (boundKind === BoundKind.COUNTY) {
        //     const county = GLOBE.INDIVIDUAL_COUNTY
        //     if (county === null) {
        //         throw new Error(xd
        //             "enterMap called with boundKind of COUNTY but GLOBE.INDIVIDUAL_COUNTY was null"
        //         )
        //     }
        //     boundMap(county)
        // }

        if (doFlyTo) {
            // Delay one frame as a workaround for the animation being cancelled on mobile
            // TODO: Figure out why this is necessary on mobile
            setTimeout(() => {
                if (signal?.aborted === true) {
                    return
                }
                const point = fitPointToViewportCenter({
                    point: { lng, lat },
                    map,
                    zoom,
                })
                map.flyTo(
                    {
                        center: point,
                        zoom,
                    },
                    { cause: "enterMap", signal }
                )
            }, 50)
        }

        // Add inverted county polygon
        const promise: Promise<Polygon> = handleLatLngClick(lat, lng, map, {
            modifyHistory,
            signal,
        })
            .then((data) => {
                signal?.throwIfAborted()
                let foundPolygon: Polygon | null = null
                data.payload.forEach((district) => {
                    if (district.type === "State District") {
                        const polygon: Polygon = JSON.parse(district.polygon)
                        const inverted = cutout(polygon, 30)
                        map.addSource("county-cutout", {
                            type: "geojson",
                            data: inverted,
                        })
                        map.addLayer({
                            id: "county-cutout",
                            type: "fill",
                            source: "county-cutout",
                            layout: {},
                            paint: {
                                "fill-color": "#444",
                                "fill-opacity": 0.8,
                            },
                        })
                        foundPolygon = polygon
                    }
                })
                if (foundPolygon === null) {
                    throw new Error("State District not found in payload")
                }
                return foundPolygon
            })
            .catch((err) => {
                console.warn({ err }, "handleLatLngClick aborted")
                // map.stop()
            })

        // if (boundKind === BoundKind.POLYGON) {
        boundMap(promise, { signal })
        if (!waitForZoomToBoundMap) {
            void enableMapBounds({ signal })
        }
        // }

        // Show OSM left panel elements
        querySelectorThrow("#filter-row").classList.remove("hidden")
        querySelectorThrow("#district-list").classList.remove("hidden")

        // Hide Globe left panel elements
        querySelectorThrow("#left-panel-header").classList.add("hidden")
        querySelectorThrow("#zip-code-container").classList.add("hidden")
    }

    let smoothlyGoToMapPointAbortController = new AbortController()

    // misc.js uses this to zoom into the map when a search bar entry is clicked
    async function smoothlyGoToMapPoint(
        lat: number | string,
        lng: number | string,
        zoom: number,
        usCounty: string,
        usState: string,
        options?: SmoothlyGoToMapPointOptions
    ): Promise<void> {
        if (typeof lat === "string") {
            lat = parseFloat(lat)
        } else if (typeof lat !== "number") {
            throw new TypeError("lat was not the correct type")
        }
        if (typeof lng === "string") {
            lng = parseFloat(lng)
        } else if (typeof lng !== "number") {
            throw new TypeError("lng was not the correct type")
        }

        const defaultOptions: SmoothlyGoToMapPointOptions = {
            modifyHistory: true,
            // abortController: smoothlyGoToMapPointAbortController,
        }
        if (options === undefined) {
            options = defaultOptions
        }
        if (usState.length === 2) {
            usState = STATE_FIPS_BY_ABBR[usState].name
        }

        smoothlyGoToMapPointAbortController.abort(
            "New smoothlyGoToMapPoint started"
        )
        smoothlyGoToMapPointAbortController = new AbortController()

        const signal = smoothlyGoToMapPointAbortController.signal

        const abbr: string = STATE_FIPS_BY_NAME[usState].abbr

        if (state.map.immersed) {
            // Case: We're already in the map
            // Disable map bounds while we fly
            disableMapBounds()
            try {
                map.removeLayer("county-cutout")
                map.removeSource("county-cutout")
            } catch (err) {
                console.error(err)
            }
            returnToGlobePopup.remove()
            // flyTo can cause us to zoom out far enough that autoLeave is triggered, so we disable it until the zoom finishes.
            disableAutoLeave()

            const point = fitPointToViewportCenter({
                point: { lng, lat },
                map,
                zoom: 13.24,
            })
            // TODO: Abort (not sure about this one)
            map.flyTo(
                { center: point, zoom: 13.24 },
                { cause: "smoothlyGoToMapPoint", signal }
            )

            handleLatLngClick(lat, lng, map, {
                modifyHistory: options.modifyHistory,
                signal,
            })
                .then((data) => {
                    signal.throwIfAborted()
                    data.payload.forEach((district) => {
                        if (district.type === "State District") {
                            const polygon: Polygon = JSON.parse(
                                district.polygon
                            )
                            const inverted = cutout(polygon, 30)
                            map.addSource("county-cutout", {
                                type: "geojson",
                                data: inverted,
                            })
                            map.addLayer({
                                id: "county-cutout",
                                type: "fill",
                                source: "county-cutout",
                                layout: {},
                                paint: {
                                    "fill-color": "#444",
                                    "fill-opacity": 0.8,
                                },
                            })

                            boundMap(polygon, { signal })
                        }
                    })
                })
                .catch((err) => {
                    console.error(err)
                })

            GLOBE.loadCounties(usState, false, {
                modifyHistory: false,
                modifyTutorial: false,
                signal,
            }).catch((err) => {
                console.error(err)
            })
        } else if (state.map.visible) {
            // Case: We're at the zip code level (map is visible, but still in the globe)
            // Use BoundKind.POLYGON to indicate that we should bound the map based on Junior's GeoJSON county polygon
            // TODO: Abort
            enterMap({
                lat,
                lng,
                zoom,
                usCounty,
                usState,
                doFlyTo: true,
                boundKind: BoundKind.POLYGON,
                waitForZoomToBoundMap: true,
                modifyHistory: options.modifyHistory,
                signal,
            })
            if (abbr === GLOBE.COUNTY_GROUP.userData.abbr) {
                return
            }
        } else {
            // Case: We're in any other globe layer
            // Begin preloading the map
            cameraSynchronizer.syncMap()
            state.map.visibleSoon = true
            if (GLOBE.areCountiesLoaded()) {
                GLOBE.leftPanel.show()
                enterMap({
                    lat,
                    lng,
                    zoom,
                    usCounty,
                    usState,
                    doFlyTo: true,
                    signal,
                })
            } else {
                GLOBE.controls.setExtraThetaOffset(0)
                GLOBE.controls.setMinDistance(53.2)
                GLOBE.controls.focusLngLat(lng, lat, 2)
                await new Promise((resolve) => setTimeout(resolve, 750))
                signal.throwIfAborted()
                GLOBE.leftPanel.show()
                enterMap({
                    lat,
                    lng,
                    zoom,
                    usCounty,
                    usState,
                    doFlyTo: true,
                    boundKind: BoundKind.POLYGON,
                    signal,
                })
            }

            // UI management
            querySelectorThrow(".navigation_list").classList.remove("hidden")
            const list = querySelectorThrow(".state_list")
            list.classList.remove("hidden")
            const button = querySelectorThrow(`.state_list ul li#${abbr}`)
            button.classList.add("highlighted")
            // TODO: Abort
            setTimeout(() => {
                if (signal.aborted) {
                    return
                }
                scrollBottomBarToHighlighted("state_list")
            }, 1000)

            // Show attribution
            const attribution = querySelectorThrow(".maplibregl-ctrl-attrib")
            attribution.classList.remove("hidden")
        }

        const navList = querySelectorThrow(".navigation_list")
        // TODO: Abort
        await new Promise((resolve) => setTimeout(resolve, 2500))
        signal.throwIfAborted()
        Promise.all([GLOBE.loadStatesPromise, GLOBE.addStateListPromise])
            // TODO: Abort
            .then(async () => {
                signal.throwIfAborted()
                // TODO: Abort
                await GLOBE.loadCounties(usState, false, {
                    modifyHistory: false,
                    modifyTutorial: false,
                    signal,
                }).catch((err) => {
                    console.log(err)
                })
            })
            // TODO: Abort
            .then(async () => {
                signal.throwIfAborted()
                // Slide canvas to the right to keep globe elements centered
                querySelectorThrow("#popup-container").classList.add(
                    "nudge-right"
                )
                querySelectorThrow("#map").classList.add("nudge-right")
                GLOBE.canvas.classList.add("nudge-right")
                GLOBE.labelRenderer.domElement.classList.add("nudge-right")
                GLOBE.globe_svg_container.classList.add("nudge-right")
                // TODO: Abort
                return await switchToCountyList(abbr)
            })
            .then(() => {
                signal.throwIfAborted()
                try {
                    const prevSelected = querySelectorThrow(
                        ".county_list .selected"
                    )
                    prevSelected.classList.remove("selected")
                } catch (e) {
                    console.log(e)
                }

                const ele = querySelectorThrow("li[id='" + usCounty + "']")
                if (ele != null) {
                    ele.classList.add("selected")
                    navList.classList.remove("hidden")
                    querySelectorThrow("#loaded").classList.remove("hidden")
                    scrollBottomBarToHighlighted("county_list")
                }
                GLOBE.dispatchFooterChangeEvent()
            })
            .then(() => {
                signal.throwIfAborted()
                // Set up county list callbacks
                document
                    .querySelectorAll(".county_list li")
                    .forEach((element) => {
                        const a = element.querySelector("span")
                        if (a === null) {
                            throw new Error(
                                "Nav list li did not have an anchor"
                            )
                        }
                        const fips = element.getAttribute("fips")
                        if (fips === null) {
                            console.error(
                                "List element did not have fips attribute"
                            )
                            return
                        }
                        const mesh = GLOBE.getMeshByFips(fips)
                        if (mesh === null) {
                            console.error("Could not get mesh by fips")
                            return
                        }
                        a.addEventListener("mouseover", (event) => {
                            GLOBE.highlight(mesh)
                        })

                        a.addEventListener("mouseout", (event) => {
                            GLOBE.unhighlight(mesh)
                        })

                        a.addEventListener("click", (event) => {
                            cameraSynchronizer.master = "globe"
                            cameraSynchronizer.syncGlobe()
                            GLOBE.animate(100000000)
                            // TODO: Abort?
                            void GLOBE.loadZips(mesh)
                            tutorial.state.send("LEAVE_MAP")
                        })
                    })
            })
            .catch((err) => {
                console.log(err)
            })
    }

    // Expose as a global variable so misc.js can use it
    window.smoothlyGoToMapPoint = smoothlyGoToMapPoint

    window.smoothlyGoToViewPoint = function (lat, lng, zoom) {
        const point = fitPointToViewportCenter({
            point: { lng, lat },
            map,
            zoom,
        })
        map.flyTo({ zoom, center: point })
    }

    resetButton.addEventListener("click", () => {
        reset()
        swapper.swapToGlobe()
    })

    GLOBE.addEventListener("loadzips", () => {
        if (state.map.immersed) {
            swapper.swapToGlobe()
            reset()
            state.map.immersed = false
            state.map.visible = true
        }
    })

    map.on("stubbornzoom", (event) => {
        // const historyState = globeHistoryState({
        //     fips: GLOBE.CLICKED?.userData.fips,
        // })
        // updateHistory(historyState)
        // if (GLOBE.isRegionSpecifiedInUrl()) {
        //     history.replaceState(
        //         { fips: GLOBE.CLICKED?.userData.fips },
        //         "",
        //         `?fips=${GLOBE.CLICKED?.userData.fips as number}`
        //     )
        // } else {
        //     history.pushState(
        //         { fips: GLOBE.CLICKED?.userData.fips },
        //         "",
        //         `?fips=${GLOBE.CLICKED?.userData.fips as number}`
        //     )
        //     // this.historyStatePushed = true;
        // }
        cameraSynchronizer.syncGlobe()
        reset()
        swapper.swapToGlobe()
        GLOBE.tutorial.state.send("LEAVE_MAP")
        state.map.immersed = false
        state.map.visible = true
    })

    document.addEventListener("mapunimmerse", () => {
        // GLOBE.dispatchEvent(
        //     new CustomEvent("regionload", {
        //         detail: {
        //             type: "zip",
        //             id: GLOBE.CLICKED.userData.fips,
        //             name: GLOBE.CLICKED.userData.name,
        //             county: GLOBE.CLICKED.userData.fips,
        //             state: GLOBE.COUNTY_GROUP.userData.state_name,
        //         },
        //     })
        // )
        // if (GLOBE.isRegionSpecifiedInUrl()) {
        //     history.replaceState(
        //         { fips: GLOBE.CLICKED.userData.fips },
        //         "",
        //         `?fips=${GLOBE.CLICKED.userData.fips}`
        //     )
        // } else {
        //     history.pushState(
        //         { fips: GLOBE.CLICKED.userData.fips },
        //         "",
        //         `?fips=${GLOBE.CLICKED.userData.fips}`
        //     )
        //     // this.historyStatePushed = true;
        // }
        // cameraSynchronizer.syncGlobe()
        // reset()
        // swapper.swapToGlobe()
        // GLOBE.tutorial.state.send("LEAVE_MAP")
        // state.map.immersed = false
        // state.map.visible = true
        querySelectorThrow<HTMLAnchorElement>(
            `#county_list li.selected a`
        ).click()
        // state.map.pendingUnimmerse = false
    })

    // initFromURLParams(GLOBE, {
    //     enterMap,
    //     tutorial,
    // })

    async function onRegionLoad(
        event: RegionLoadEvent,
        firstSymbolId: string,
        firstLineId: string
    ): Promise<void> {
        if (lastLoaded !== null) {
            const { id } = lastLoaded
            map.offAll()
            map.removeLayer(`${id}-fills`)
            map.removeLayer(`${id}-borders`)
            try {
                map.removeSource(id)
            } catch (err) {
                console.error(err)
            }
            zipLabel.remove()
        }
        lastLoad = event.detail
        const thisLoad = event.detail

        const { id, name } = event.detail
        const usState = event.detail.state

        // Immediately set the left panel county name
        GLOBE.leftPanel.setCounty(name)

        // When the user moves their mouse over the state-fill layer, we'll update the
        // feature state for the feature under the mouse.
        const onMouseMove = function (
            e: maplibregl.MapMouseEvent & {
                features?: maplibregl.MapGeoJSONFeature[] | undefined
                // eslint-disable-next-line @typescript-eslint/ban-types
            } & Object
        ): void {
            if (typeof e.features === "undefined") return
            if (e.features.length > 0) {
                if (
                    hoveredStateId !== null &&
                    hoveredStateId !== e.features[0].id
                ) {
                    // Un-highlight the old feature
                    map.setFeatureState(
                        {
                            source: id,
                            id: hoveredStateId,
                        },
                        { hover: false }
                    )
                    zipLabel.remove()
                }
                if (hoveredStateId !== e.features[0].id) {
                    onMouseEnterZip(e.features[0].properties.GEOID20)
                    // Highlight the new feature
                    hoveredStateId = e.features[0].id as string | number
                    map.setFeatureState(
                        { source: id, id: hoveredStateId as string },
                        { hover: true }
                    )
                }
            }
        }
        map.on("mousemove", "test", (event) => {})

        // When the mouse leaves the state-fill layer, update the feature state of the
        // previously hovered feature.
        const onMouseLeave = function (): void {
            if (hoveredStateId !== null) {
                map.setFeatureState(
                    { source: id, id: hoveredStateId },
                    { hover: false }
                )
            }
            hoveredStateId = null
            zipLabel.remove()
        }

        const onClick = function (event): void {
            event.preventDefault()
            const { lng, lat } = event.lngLat
            enterMap({ lat, lng, zoom: 13.24, usCounty: name, usState })
        }

        const response = await fetch(
            `/assets/geojson/zip-intersection/GEOID_${id}.geojson`
        )

        // Error checks
        if (!response.ok) {
            console.error("Response not ok", response)
            return
        }
        if (lastLoad !== thisLoad) {
            console.warn("Load", thisLoad, "superceded by new load", lastLoad)
            return
        }

        const geojson = await response.json()

        labelPositions = getLabelPositions(geojson, 0.00001)

        map.addSource(id, {
            type: "geojson",
            data: geojson,
            generateId: true,
        })
        lastLoaded = lastLoad
        map.addLayer(
            {
                id: `${id}-fills`,
                type: "fill",
                source: id,
                layout: {},
                paint: {
                    "fill-color": "#9F9888",
                    "fill-opacity": [
                        "case",
                        ["boolean", ["feature-state", "hover"], false],
                        1,
                        0.1,
                    ],
                },
            },
            firstLineId
        )
        map.addLayer(
            {
                id: `${id}-borders`,
                type: "line",
                source: id,
                layout: {},
                paint: {
                    "line-color": "#9F9888",
                    "line-width": 3,
                    "line-opacity": 1,
                },
            },
            firstSymbolId
        )
        map.trackedOn("mousemove", `${id}-fills`, onMouseMove)
        map.trackedOn("mouseleave", `${id}-fills`, onMouseLeave)
        map.trackedOn("click", `${id}-fills`, onClick)

        GLOBE.leftPanel.setupZipButtons(
            geojson,
            // On mouse enter a zip button
            (zip, featureId) => {
                onMouseEnterZip(zip)
                hoveredStateId = featureId
                map.setFeatureState(
                    { source: id, id: hoveredStateId as string | number },
                    { hover: true }
                )
            },
            // On mouse leave a zip button
            onMouseLeave,
            // On zip button click
            (zip, id) => {
                if (labelPositions === null) {
                    console.error("labelPositions was null")
                    return
                }
                const res = labelPositions.get(zip)
                if (res === undefined) {
                    console.error("Could not get label positions from zip")
                    return
                }
                const [lng, lat] = res
                enterMap({ lat, lng, zoom: 13.24, usCounty: name, usState })
            }
        )
    }
    return { onRegionLoad, enterMap }
}

function waitAndSetUpUICallbacks(
    nTimes = 0,
    options: { tutorial: LandingPageTutorial }
): void {
    // TODO: Set this up with a proper promise-based system that fires after the state list is loaded
    // console.log(`wait ${nTimes}`);

    if (nTimes >= 300) {
        console.log(
            "ERROR: Waited 30 seconds but state list didn't load in. Giving up."
        )
        return
    }

    const stateList = document.getElementById("state_list")

    if (stateList === null) {
        console.log("ERROR: state_list not found, could not setup callbacks")
        // Wait another 100ms for the state list to load in
        setTimeout(() => {
            waitAndSetUpUICallbacks(nTimes + 1, options)
        })
        return
    }

    const buttons = stateList.querySelectorAll("span")

    if (buttons === null || buttons.length === 0) {
        console.log("ERROR: buttons length is 0, could not setup callbacks")
        // Wait another 100ms for the state list to load in
        setTimeout(() => {
            waitAndSetUpUICallbacks(nTimes + 1, options)
        })
        return
    }

    const nChildren = buttons.length
    for (let i = 0; i < nChildren; i++) {
        buttons[i].addEventListener("click", function (e) {
            if (state.map.immersed) {
                cameraSynchronizer.master = "globe"
                cameraSynchronizer.syncGlobe()
                GLOBE.animate(100000000)
                swapper.swapToGlobe()
                options.tutorial.state.send("LEAVE_MAP")
            }
            const stateName = this.innerText
            const stateMesh = GLOBE.getStateByName(stateName)
            void GLOBE.loadCounties(stateMesh)
        })
        buttons[i].addEventListener("mouseover", function () {
            const stateName = this.innerText
            const stateMesh = GLOBE.getStateByName(stateName)
            GLOBE.highlight(stateMesh)
        })

        buttons[i].addEventListener("mouseout", function () {
            const stateName = this.innerText
            const stateMesh = GLOBE.getStateByName(stateName)
            GLOBE.unhighlight(stateMesh)
        })
    }
}

function onWindowResize(): void {
    renderer.setSize(
        document.documentElement.clientWidth,
        document.documentElement.clientHeight
    )
    const aspectRatio =
        document.documentElement.clientWidth /
        document.documentElement.clientHeight

    GLOBE.onWindowResize()

    // const fov = MathHelpers.horizontalToVerticalFOV(
    //     GLOBE.camera.fov,
    //     GLOBE.camera.aspect
    // )
    map.transform._fov = GLOBE.camera.fov * THREE.MathUtils.DEG2RAD
    map.transform._unmodified = false
    map.transform._calcMatrices()

    if (aspectRatio < 1.55) {
        const canvas = querySelectorThrow("canvas")
        canvas.classList.add("tall")
    } else {
        const canvas = querySelectorThrow("canvas")
        canvas.classList.remove("tall")
    }
    if (screenQuad.material instanceof THREE.ShaderMaterial) {
        screenQuad.material.uniforms.screenAspect.value = GLOBE.camera.aspect
    }
}

/**
 * Frame entrypoint
 * @param elapsed
 */
function animate(elapsed): void {
    // stats.begin()
    requestAnimationFrame(animate)

    if (!window.paused) {
        const delta = fpsClock.getDelta()
        elapsedTime += delta

        TEXTURE_INITIALIZER.update()

        if (swapper === undefined || swapper.globeVisible) {
            GLOBE.animate(elapsedTime)
        } else {
            GLOBE.updateTweens()
        }

        if (state.map.visible || state.map.visibleSoon) {
            cameraSynchronizer?.update()
        }

        cameraSynchronizer?.updateTweens()

        if (swapper === undefined || swapper.globeVisible) {
            render()
        } else {
            GLOBE.controls.update()
        }
    }
    // stats.end()
}

function render(): void {
    // Render globe
    GLOBE.render(renderer)
}
